/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Toolbar,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import GlobalAdminSidebar from "../../../../Components/GlobalAdminSidebar/GlobalAdminSidebar";
import Topheader from "../../../../Layout/header/header";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import {
  createGlobalAdminFlexMetadata,
  fetchGlobalAdminFlexMetadata,
  updateGlobalAdminFlexMetadata,
  deleteGlobalAdminFlexMetadata,
} from "../../../../Services/redux/global-admin-users/flex metadata/GlobalAdminFlexActions";

// SDD86MARKETPLACE1098R120:
// CODE86MARKETPLACE1098R120: Manage Flex Meta Data as a Harman Admin
// Related items (auto-generated):
// End related items

const GlobalAdminFlexMetadata = () => {
  const [addOpen, setAddOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [flexName, setFlexName] = useState("");
  const [flexDesc, setFlexDesc] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    id: null,
    name: "",
    description: "Description goes here....",
  });
  const [editItemId, setEditItemId] = useState(null);
  const { flexMetadata = [], loading: loader } = useSelector(
    (state) => state.globalAdminFlexReducers || {}
  );
  const flexNameOptions = (flexMetadata || [])
    .map((item) => item?.name || "")
    .filter(Boolean);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchGlobalAdminFlexMetadata());
  }, [dispatch]);

  const nameRegex = /^[A-Za-z0-9_-]+$/;
  const existingNames = (flexMetadata || [])
    .map((i) => (i?.name || "").toLowerCase())
    .filter(Boolean);
  const nameHasValue = flexName.trim().length > 0;
  const isNamePatternValid = nameHasValue ? nameRegex.test(flexName) : false;
  const isNameDuplicate =
    !isEditMode && nameHasValue
      ? existingNames.includes(flexName.toLowerCase())
      : false;

  let nameErrorText = "";
  if (!isEditMode && nameHasValue && !isNamePatternValid) {
    nameErrorText = "No spaces or special characters allowed except for - and _";
  } else if (!isEditMode && nameHasValue && isNameDuplicate) {
    nameErrorText = "This flex metadata already exists.";
  }

  const buildFlexPayload = () => ({
    name: (flexName || "").trim(),
    description: (flexDesc || "").trim(),
    domain: "NONE",
  });

  const handleSaveFlexMetadata = () => {
    const payload = buildFlexPayload();
    dispatch(createGlobalAdminFlexMetadata(payload));
    setAddOpen(false);
    setIsEditMode(false);
    setFlexName("");
    setFlexDesc("");
    setEditItemId(null);
  };

  const handleUpdateFlexMetadata = (id) => {
    const payload = buildFlexPayload();
    dispatch(updateGlobalAdminFlexMetadata(payload, id));
    setAddOpen(false);
    setIsEditMode(false);
    setFlexName("");
    setFlexDesc("");
    setEditItemId(null);
  };

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Toolbar />

      <Box
        sx={{
          display: "flex",
          width: "100%",
        }}
      >
        <GlobalAdminSidebar />

        {/* Main Content */}
        <Box
          sx={{
            marginLeft: "320px",
            flex: 1,
            padding: "16px 32px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "860px" }}>
            {/* Header row with title and action button */}

            <Box
              sx={{
                width: "100%",
                maxWidth: "860px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
                    Flex Metadata
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      minWidth: "177px",
                      minHeight: "36px",
                      textTransform: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => {
                      // Open in add mode with blank fields
                      setIsEditMode(false);
                      setFlexName("");
                      setFlexDesc("");
                      setEditItemId(null);
                      setAddOpen(true);
                    }}
                  >
                    Add New Flex Metadata
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Helper text */}
            <Typography sx={{ mt: 1, fontSize: "14px", color: "#717273" }}>
              Max of 100 flex metadata can be added per portfolio.
            </Typography>

            {/* List section */}
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{ fontWeight: 600, fontSize: "16px", color: "#2A2C2D" }}
              >
                List of Flex Metadatas
              </Typography>

              <Paper sx={{ mt: 2 }}>
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      borderRight: "none",
                      borderLeft: "none",
                    },
                    "& td, & th": {
                      borderRight: "none !important",
                      borderLeft: "none !important",
                    },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#1214150D" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Metadata</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ width: 120, fontWeight: 600 }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(flexMetadata || []).map((row) => (
                      <TableRow key={row?.id || row?._id || row?.name}>
                        <TableCell>

                  {row?.name && (
                  <Tooltip title={row?.name}>
                <Typography sx={{ 
                         fontSize: "14px", 
                         fontWeight: 500,
                         overflow: "hidden",
                         textOverflow: "ellipsis",
                         whiteSpace: "nowrap" }}>
                {row?.name.length > 25
                  ? `${row?.name.substring(0, 25)}...`
                  : row?.name}
              </Typography>
              </Tooltip>
                )}
                          
                          </TableCell>
                        <TableCell>
                          
             {row?.description && (
            <Tooltip title={row?.description}>
              <Typography sx={{ 
                         fontSize: "14px", 
                         fontWeight: 500,
                         overflow: "hidden",
                         textOverflow: "ellipsis",
                         whiteSpace: "nowrap" }}>
                {row?.description.length > 25
                  ? `${row?.description.substring(0, 50)}...`
                  : row?.description}
              </Typography>
            </Tooltip>
          )}   
          </TableCell>

                          {/* {row?.description || ""}</TableCell> */}
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            aria-label="delete"
                            onClick={() => {
                              setSelectedItem({
                                id: row?.id || row?._id || row?.labelId || null,
                                name: row?.name,
                                description: row?.description || "",
                              });
                              setDeleteOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ ml: 1 }}
                            aria-label="edit"
                            onClick={() => {
                              // Prefill the Add modal with selected row values
                              setFlexName(row?.name || "");
                              setFlexDesc(row?.description || "");
                              setEditItemId(
                                row?.id || row?._id || row?.labelId || null
                              );
                              setIsEditMode(true);
                              setAddOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
              {/* Add New Flex Metadata Modal */}
              <Dialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 500,
                    fontSize: "24px",
                  }}
                >
                  {isEditMode ? "Edit Flex Metadata" : "Add New Flex Metadata"}
                  <IconButton onClick={() => setAddOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  {/* Flex Metadata Name: input + dropdown (multi-purpose bar) */}
                  <Typography
                    sx={{ fontWeight: 500, mb: 1, mt: 1, fontSize: "16px" }}
                  >
                    Flex Metadata Name{" "}
                    <span style={{ color: "#E53935" }}>*</span>
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={flexNameOptions}
                    value={flexName}
                    onChange={(e, val) => {
                      if (isEditMode) return;
                      setFlexName(val || "");
                    }}
                    onInputChange={(e, val) => {
                      if (isEditMode) return;
                      setFlexName(val || "");
                    }}
                    disabled={isEditMode}
                    slotProps={{
                      listbox: {
                        style: { maxHeight: 142, overflowY: "auto" },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Eg: ICON_DOLBY"
                        error={!isEditMode && !!nameErrorText}
                        helperText={isEditMode ? "" : nameErrorText}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            readOnly: isEditMode,
                          },
                        }}
                      />
                    )}
                  />

                  {/* Description: textarea */}
                  <Typography
                    sx={{ fontWeight: 500, mb: 1, mt: 3, fontSize: "16px" }}
                  >
                    Description <span style={{ color: "#E53935" }}>*</span>
                  </Typography>
                  <TextareaAutosize
                    minRows={3}
                    placeholder="Prompt text"
                    value={flexDesc}
                    onChange={(e) => setFlexDesc(e.target.value)}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid #E0E0E0",
                      borderRadius: 4,
                      padding: "12px 14px",
                      fontSize: 16,
                      fontFamily: "inherit",
                      color: "inherit",
                      lineHeight: 1.5,
                      resize: "vertical",
                      overflowX: "hidden",
                    }}
                  />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "flex-end", pb: 2 }}>
                  <Button
                    onClick={() => {
                      setAddOpen(false);
                      setIsEditMode(false);
                      setFlexName("");
                      setFlexDesc("");
                      setEditItemId(null);
                    }}
                    variant="outlined"
                    sx={{ background: "#f5f5f5", color: "#222" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      isEditMode
                        ? () => handleUpdateFlexMetadata(editItemId)
                        : handleSaveFlexMetadata
                    }
                    variant="contained"
                    disabled={
                      isEditMode
                        ? !flexDesc.trim()
                        : !flexName.trim() ||
                          !flexDesc.trim() ||
                          !isNamePatternValid ||
                          isNameDuplicate
                    }
                  >
                    {isEditMode ? "Update" : "Save"}
                  </Button>
                </DialogActions>
              </Dialog>
              {/* Delete Flex Metadata Modal */}
              <Dialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 500,
                    fontSize: "24px",
                  }}
                >
                  Delete Flex Metadata
                  <IconButton onClick={() => setDeleteOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: "#F5F5F5" }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{ fontSize: "12px", color: "#717273", mb: 0.5 }}
                        >
                          Metadata
                        </Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedItem.name}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{ fontSize: "12px", color: "#717273", mb: 0.5 }}
                        >
                          Description
                        </Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                          {selectedItem.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                  <Typography
                    sx={{ mt: 1, mb: 2, fontSize: "16px", color: "#222" }}
                  >
                    Are you sure you wish to permanently remove this Flex
                    Metadata?
                    <br />
                    <span style={{ color: "#717273" }}>
                      This action cannot be reversed.
                    </span>
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "flex-end", pb: 2 }}>
                  <Button
                    onClick={() => setDeleteOpen(false)}
                    variant="outlined"
                    sx={{ background: "#f5f5f5", color: "#222" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectedItem?.id) {
                        dispatch(
                          deleteGlobalAdminFlexMetadata(selectedItem.id)
                        );
                      }
                      setDeleteOpen(false);
                    }}
                    variant="outlined"
                    sx={{
                      border: "1px solid #E53935",
                      color: "#E53935",
                      background: "#fff",
                    }}
                  >
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GlobalAdminFlexMetadata;
